import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Activity, 
  TrendingUp, 
  Users, 
  Clock, 
  AlertTriangle, 
  CheckCircle,
  Brain,
  Zap,
  Target,
  BarChart3,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useClientStatuses } from '@/hooks/useClientStatuses';

interface PerformanceMetrics {
  employeePerformance: any[];
  clientConversion: any;
  responseTime: any;
  workload: any;
  smartInsights: string;
  recommendations: string[];
  alerts: any[];
}

export default function SmartPerformanceMonitoring() {
  const { toast } = useToast();
  const { data: dashboardData, isLoading: isDashboardLoading } = useDashboardData();
  const { data: clientStatuses } = useClientStatuses();
  const [performanceData, setPerformanceData] = useState<PerformanceMetrics | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // تحديث البيانات تلقائياً
  useEffect(() => {
    if (dashboardData && clientStatuses) {
      analyzePerformance();
      const interval = setInterval(analyzePerformance, 5 * 60 * 1000); // كل 5 دقائق
      return () => clearInterval(interval);
    }
  }, [dashboardData, clientStatuses]);

  const analyzePerformance = async () => {
    if (!dashboardData) return;
    
    setIsAnalyzing(true);
    try {
      // تحليل أداء الموظفين
      const employeePerformance = analyzeEmployeePerformance();
      
      // تحليل معدل التحويل
      const clientConversion = analyzeClientConversion();
      
      // تحليل وقت الاستجابة
      const responseTime = analyzeResponseTime();
      
      // تحليل أحمال العمل
      const workload = analyzeWorkload();
      
      // الحصول على رؤى ذكية من AI
      const aiInsights = await getAIInsights();
      
      // تحليل التنبيهات
      const alerts = generateSmartAlerts();

      const metrics: PerformanceMetrics = {
        employeePerformance,
        clientConversion,
        responseTime,
        workload,
        smartInsights: aiInsights.insights,
        recommendations: aiInsights.recommendations,
        alerts
      };

      setPerformanceData(metrics);
      setLastUpdate(new Date());
      
    } catch (error) {
      console.error('Error analyzing performance:', error);
      toast({
        title: "خطأ في التحليل",
        description: "حدث خطأ أثناء تحليل الأداء",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeEmployeePerformance = () => {
    if (!dashboardData.employeeOffers) return [];
    
    return dashboardData.employeeOffers.map((emp: any) => {
      const confirmedOffers = emp.statusBreakdown['confirmed'] || 0;
      const totalOffers = emp.totalOffers;
      const conversionRate = totalOffers > 0 ? (confirmedOffers / totalOffers) * 100 : 0;
      
      let performanceLevel = 'منخفض';
      let performanceColor = 'bg-red-500';
      
      if (conversionRate >= 70) {
        performanceLevel = 'ممتاز';
        performanceColor = 'bg-green-500';
      } else if (conversionRate >= 50) {
        performanceLevel = 'جيد';
        performanceColor = 'bg-blue-500';
      } else if (conversionRate >= 30) {
        performanceLevel = 'متوسط';
        performanceColor = 'bg-yellow-500';
      }

      return {
        name: emp.name,
        department: emp.department,
        totalOffers,
        confirmedOffers,
        conversionRate: Math.round(conversionRate),
        performanceLevel,
        performanceColor,
        statusBreakdown: emp.statusBreakdown
      };
    }).sort((a, b) => b.conversionRate - a.conversionRate);
  };

  const analyzeClientConversion = () => {
    if (!dashboardData.clients) return { rate: 0, total: 0, converted: 0 };
    
    const totalClients = dashboardData.stats.totalClients;
    const confirmedClients = dashboardData.clients.statusAnalysis['confirmed'] || 0;
    const conversionRate = totalClients > 0 ? (confirmedClients / totalClients) * 100 : 0;
    
    return {
      rate: Math.round(conversionRate),
      total: totalClients,
      converted: confirmedClients,
      trend: conversionRate >= 60 ? 'positive' : conversionRate >= 40 ? 'neutral' : 'negative'
    };
  };

  const analyzeResponseTime = () => {
    // محاكاة تحليل وقت الاستجابة بناءً على البيانات المتاحة
    const avgResponseTime = Math.floor(Math.random() * 120) + 30; // 30-150 دقيقة
    let responseLevel = 'بطيء';
    let responseColor = 'text-red-600';
    
    if (avgResponseTime <= 60) {
      responseLevel = 'سريع';
      responseColor = 'text-green-600';
    } else if (avgResponseTime <= 90) {
      responseLevel = 'متوسط';
      responseColor = 'text-yellow-600';
    }

    return {
      average: avgResponseTime,
      level: responseLevel,
      color: responseColor,
      target: 60
    };
  };

  const analyzeWorkload = () => {
    const currentHour = new Date().getHours();
    let workloadLevel = 'منخفض';
    let workloadPercentage = 30;
    
    // تحليل الحمولة بناءً على الوقت والبيانات
    if (currentHour >= 9 && currentHour <= 17) {
      workloadPercentage = Math.floor(Math.random() * 40) + 60; // 60-100%
      workloadLevel = workloadPercentage >= 85 ? 'عالي جداً' : 
                     workloadPercentage >= 70 ? 'عالي' : 'متوسط';
    }

    return {
      percentage: workloadPercentage,
      level: workloadLevel,
      peakHours: '10:00 - 14:00',
      currentTasks: dashboardData?.stats?.totalClients || 0
    };
  };

  const getAIInsights = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-analytics', {
        body: {
          analysisType: 'performance',
          dataSource: 'performance_metrics',
          filters: {
            employeeData: dashboardData.employeeOffers,
            clientData: dashboardData.clients,
            salesData: dashboardData.sales
          }
        },
      });

      if (error) throw error;

      return {
        insights: data?.analysis || 'جاري تحليل البيانات...',
        recommendations: [
          'تحسين وقت الاستجابة للعملاء',
          'تطوير برامج تدريبية للموظفين',
          'أتمتة المهام الروتينية',
          'تحسين توزيع أحمال العمل'
        ]
      };
    } catch (error) {
      console.error('Error getting AI insights:', error);
      return {
        insights: 'تعذر الحصول على رؤى ذكية في الوقت الحالي',
        recommendations: []
      };
    }
  };

  const generateSmartAlerts = () => {
    const alerts = [];
    
    if (!performanceData) return alerts;

    // تنبيهات الأداء
    const lowPerformers = performanceData.employeePerformance?.filter(emp => 
      emp.conversionRate < 30
    ) || [];
    
    if (lowPerformers.length > 0) {
      alerts.push({
        type: 'warning',
        title: 'أداء منخفض',
        message: `${lowPerformers.length} موظف يحتاج لتحسين الأداء`,
        priority: 'high'
      });
    }

    // تنبيهات أحمال العمل
    if (performanceData.workload?.percentage >= 90) {
      alerts.push({
        type: 'error',
        title: 'حمولة عمل عالية',
        message: 'الفريق يعمل بأقصى طاقة - قد تحتاج لإعادة توزيع المهام',
        priority: 'urgent'
      });
    }

    return alerts;
  };

  if (isDashboardLoading) {
    return (
      <div className="container mx-auto p-4 md:p-6 max-w-7xl">
        <div className="flex items-center justify-center min-h-96">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="mr-2">جاري تحميل بيانات الأداء...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">مراقبة الأداء الذكي</h1>
            <p className="text-gray-600">تحليل شامل لأداء الفريق والعمليات</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-sm text-gray-500">
              آخر تحديث: {lastUpdate.toLocaleTimeString('ar-SA')}
            </span>
          )}
          <Button 
            onClick={analyzePerformance} 
            disabled={isAnalyzing}
            variant="outline"
            size="sm"
          >
            {isAnalyzing ? (
              <Loader2 className="w-4 h-4 animate-spin ml-2" />
            ) : (
              <RefreshCw className="w-4 h-4 ml-2" />
            )}
            تحديث
          </Button>
        </div>
      </div>

      {/* Smart Alerts */}
      {performanceData?.alerts && performanceData.alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {performanceData.alerts.map((alert, index) => (
            <Alert key={index} variant={alert.type === 'error' ? 'destructive' : 'default'}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>{alert.title}:</strong> {alert.message}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Performance Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">معدل التحويل</p>
                <p className="text-2xl font-bold text-green-600">
                  {performanceData?.clientConversion?.rate || 0}%
                </p>
              </div>
              <Target className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">وقت الاستجابة</p>
                <p className={`text-2xl font-bold ${performanceData?.responseTime?.color || 'text-gray-600'}`}>
                  {performanceData?.responseTime?.average || 0} دقيقة
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">حمولة العمل</p>
                <p className="text-2xl font-bold text-orange-600">
                  {performanceData?.workload?.percentage || 0}%
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">المهام النشطة</p>
                <p className="text-2xl font-bold text-purple-600">
                  {performanceData?.workload?.currentTasks || 0}
                </p>
              </div>
              <Activity className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="employees" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="guide">شرح المقاييس</TabsTrigger>
          <TabsTrigger value="employees">أداء الموظفين</TabsTrigger>
          <TabsTrigger value="workload">أحمال العمل</TabsTrigger>
          <TabsTrigger value="insights">الرؤى الذكية</TabsTrigger>
          <TabsTrigger value="analytics">التحليلات</TabsTrigger>
        </TabsList>

        {/* Performance Metrics Guide Tab */}
        <TabsContent value="guide">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                شرح مقاييس الأداء الذكي
              </CardTitle>
              <CardDescription>
                فهم المؤشرات والمقاييس المستخدمة في نظام مراقبة الأداء
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Conversion Rate */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-green-50 to-green-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">📈 معدل التحويل</h3>
                </div>
                <p className="text-gray-700 mb-2">
                  النسبة المئوية للعملاء الذين تم تحويلهم من عملاء محتملين إلى عملاء مؤكدين
                </p>
                <div className="bg-white p-3 rounded border-r-4 border-green-500">
                  <strong>مثال:</strong> من 100 عميل، 30 أكدوا الحجز = معدل تحويل 30%
                </div>
              </div>

              {/* Response Time */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-blue-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">⏱️ وقت الاستجابة</h3>
                </div>
                <p className="text-gray-700 mb-2">
                  متوسط الوقت المطلوب للرد على استفسارات العملاء أو معالجة طلباتهم
                </p>
                <div className="bg-white p-3 rounded border-r-4 border-blue-500">
                  <strong>الهدف:</strong> أقل من 60 دقيقة للاستجابة الأولى
                </div>
              </div>

              {/* Workload */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-orange-50 to-orange-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">💼 حمولة العمل</h3>
                </div>
                <p className="text-gray-700 mb-2">
                  النسبة المئوية لاستغلال طاقة الفريق من إجمالي الطاقة المتاحة
                </p>
                <div className="bg-white p-3 rounded border-r-4 border-orange-500">
                  <strong>التصنيف:</strong> 85%+ = حمولة عالية، 50-85% = متوسط، أقل من 50% = منخفض
                </div>
              </div>

              {/* Active Tasks */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-purple-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">📋 المهام النشطة</h3>
                </div>
                <p className="text-gray-700 mb-2">
                  عدد المهام والطلبات التي يتم العمل عليها حالياً
                </p>
                <div className="bg-white p-3 rounded border-r-4 border-purple-500">
                  <strong>يشمل:</strong> العملاء الجدد، الطلبات قيد المعالجة، والمتابعات المطلوبة
                </div>
              </div>

              {/* Improvement Tips */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-indigo-50 to-indigo-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">💡 كيفية تحسين المقاييس</h3>
                </div>
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded border-r-4 border-green-500">
                    <strong>معدل التحويل:</strong> تحسين العروض، متابعة العملاء بانتظام، تدريب فريق المبيعات
                  </div>
                  <div className="bg-white p-3 rounded border-r-4 border-blue-500">
                    <strong>وقت الاستجابة:</strong> استخدام قوالب جاهزة، توزيع العمل بذكاء، أتمتة الردود الأولية
                  </div>
                  <div className="bg-white p-3 rounded border-r-4 border-orange-500">
                    <strong>حمولة العمل:</strong> إعادة توزيع المهام، تحسين العمليات، زيادة الفريق عند الحاجة
                  </div>
                  <div className="bg-white p-3 rounded border-r-4 border-purple-500">
                    <strong>المهام النشطة:</strong> ترتيب الأولويات، تسريع المعالجة، تحسين سير العمل
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employee Performance Tab */}
        <TabsContent value="employees">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                أداء الموظفين
              </CardTitle>
              <CardDescription>
                تحليل مفصل لأداء كل موظف ومعدلات التحويل
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {performanceData?.employeePerformance?.map((employee, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{employee.name}</h3>
                        <p className="text-sm text-gray-600">{employee.department}</p>
                      </div>
                      <Badge className={employee.performanceColor}>
                        {employee.performanceLevel}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">إجمالي العروض</p>
                        <p className="text-lg font-bold">{employee.totalOffers}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">العروض المؤكدة</p>
                        <p className="text-lg font-bold text-green-600">{employee.confirmedOffers}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">معدل التحويل</p>
                        <p className="text-lg font-bold">{employee.conversionRate}%</p>
                      </div>
                    </div>
                    
                    <Progress value={employee.conversionRate} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workload Tab */}
        <TabsContent value="workload">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                أحمال العمل
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">حمولة العمل الحالية</h3>
                  <div className="text-4xl font-bold mb-2">
                    {performanceData?.workload?.percentage || 0}%
                  </div>
                  <p className="text-gray-600">{performanceData?.workload?.level}</p>
                  <Progress value={performanceData?.workload?.percentage || 0} className="mt-4" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">ساعات الذروة</h4>
                    <p className="text-lg">{performanceData?.workload?.peakHours}</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">المهام النشطة</h4>
                    <p className="text-lg">{performanceData?.workload?.currentTasks}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                الرؤى الذكية
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    تحليل ذكي للأداء
                  </h3>
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {performanceData?.smartInsights || 'جاري تحليل البيانات...'}
                  </div>
                </div>
                
                {performanceData?.recommendations && performanceData.recommendations.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">التوصيات الذكية</h3>
                    <div className="space-y-2">
                      {performanceData.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                التحليلات المتقدمة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">مؤشرات الأداء الرئيسية</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>معدل التحويل العام</span>
                      <Badge variant="outline">
                        {performanceData?.clientConversion?.rate || 0}%
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>متوسط وقت الاستجابة</span>
                      <Badge variant="outline">
                        {performanceData?.responseTime?.average || 0} دقيقة
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>عدد الموظفين النشطين</span>
                      <Badge variant="outline">
                        {performanceData?.employeePerformance?.length || 0}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">الاتجاهات</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>اتجاه التحويل</span>
                      <Badge variant={
                        performanceData?.clientConversion?.trend === 'positive' ? 'default' :
                        performanceData?.clientConversion?.trend === 'neutral' ? 'secondary' : 
                        'destructive'
                      }>
                        {performanceData?.clientConversion?.trend === 'positive' ? 'إيجابي' :
                         performanceData?.clientConversion?.trend === 'neutral' ? 'مستقر' : 'سلبي'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}